import React, { useState } from 'react';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import {
  Users,
  TrendingUp,
  MapPin,
  Clock,
  Download,
  UserCheck
} from 'lucide-react';

const StudentsAnalytics = () => {
  const { analytics, exportAnalytics } = useAnalytics();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedCountry, setSelectedCountry] = useState('all');

  const demographics = analytics.students?.demographics || {};
  const engagement = analytics.students?.engagement || {};
  const retention = analytics.students?.retention || {};

  const filteredCountries = demographics.countries?.filter(country => 
    selectedCountry === 'all' || country.name === selectedCountry
  ) || [];

  const handleExport = () => {
    const result = exportAnalytics('csv');
    if (result.success) {
      alert('Données des étudiants exportées avec succès !');
    } else {
      alert('Erreur lors de l\'export');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header avec contrôles */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics des Étudiants</h2>
          <p className="text-gray-600">Démographie et comportement des étudiants</p>
        </div>
        
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="year">Cette année</option>
          </select>
          
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">Tous les pays</option>
            {demographics.countries?.map((country, index) => (
              <option key={index} value={country.name}>{country.name}</option>
            ))}
          </select>
          
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            <Download className="w-4 h-4" />
            Exporter
          </button>
        </div>
      </div>

      {/* Métriques d'engagement */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Utilisateurs Actifs</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.overview?.activeUsers || 0}</p>
              <p className="text-sm text-green-600">+12% vs mois dernier</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Nouveaux Étudiants</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.overview?.newStudentsThisMonth || 0}</p>
              <p className="text-sm text-gray-500">Ce mois</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Taux de Rétention</p>
              <p className="text-3xl font-bold text-gray-900">{retention.day30 || 0}%</p>
              <p className="text-sm text-gray-500">30 jours</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Taux de Completion</p>
              <p className="text-3xl font-bold text-gray-900">{analytics.overview?.completionRate || 0}%</p>
              <p className="text-sm text-gray-500">Moyenne</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Démographie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Âge</h3>
          <div className="space-y-4">
            {demographics.ageGroups?.map((group, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 bg-primary rounded-full"></div>
                  <span className="text-sm text-gray-900">{group.range} ans</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${group.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-12 text-right">{group.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Genre</h3>
          <div className="space-y-4">
            {demographics.genders?.map((gender, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full ${index === 0 ? 'bg-blue-500' : 'bg-pink-500'}`}></div>
                  <span className="text-sm text-gray-900">{gender.type}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${index === 0 ? 'bg-blue-500' : 'bg-pink-500'}`}
                      style={{ width: `${gender.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-12 text-right">{gender.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Répartition géographique */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition Géographique</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCountries.map((country, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{country.name}</h4>
                  <p className="text-sm text-gray-600">{country.count} étudiants</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{country.percentage}%</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Métriques de rétention */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Métriques de Rétention</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl font-bold text-green-600">{retention.day1 || 0}%</span>
            </div>
            <h4 className="font-medium text-gray-900">Jour 1</h4>
            <p className="text-sm text-gray-600">Retour le lendemain</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl font-bold text-blue-600">{retention.day7 || 0}%</span>
            </div>
            <h4 className="font-medium text-gray-900">Semaine 1</h4>
            <p className="text-sm text-gray-600">Retour après 7 jours</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl font-bold text-purple-600">{retention.day30 || 0}%</span>
            </div>
            <h4 className="font-medium text-gray-900">Mois 1</h4>
            <p className="text-sm text-gray-600">Retour après 30 jours</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl font-bold text-yellow-600">{retention.day90 || 0}%</span>
            </div>
            <h4 className="font-medium text-gray-900">Mois 3</h4>
            <p className="text-sm text-gray-600">Retour après 90 jours</p>
          </div>
        </div>
      </div>

      {/* Engagement temporel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilisateurs Actifs Quotidiens</h3>
          <div className="space-y-3">
            {engagement.dailyActiveUsers?.slice(-7).map((day, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short' })}
                </span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full" 
                      style={{ width: `${(day.users / 400) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-12 text-right">{day.users}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Utilisateurs Actifs Hebdomadaires</h3>
          <div className="space-y-3">
            {engagement.weeklyActiveUsers?.map((week, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{week.week}</span>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{ width: `${(week.users / 2500) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-12 text-right">{week.users}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentsAnalytics;
